import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Link,
  Paper,
} from '@mui/material';
import identityService from '../../services/identityService';
import '../IdentityLogin/styles.css';

function IdentityForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const identityUrl = process.env.REACT_APP_IDENTITY_URL || "https://auth.rentbeets.com";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      await identityService.forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      // Don't reveal whether email exists for security
      // Always show success to prevent email enumeration
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="identity-page">
        <Container maxWidth="sm">
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
              <Typography
                component="h1"
                variant="h4"
                align="center"
                sx={{
                  fontFamily: 'Bitter, serif',
                  color: '#8B3040',
                  marginBottom: 3,
                }}
              >
                RentBeets
              </Typography>

              <Alert severity="success" sx={{ marginBottom: 3 }}>
                Password reset email sent
              </Alert>

              <Typography variant="body1" align="center" sx={{ marginBottom: 3 }}>
                If an account exists with the email <strong>{email}</strong>, you will receive
                a password reset link shortly. Please check your inbox and spam folder.
              </Typography>

              <Box sx={{ textAlign: 'center' }}>
                <Link
                  href={`${identityUrl}/identity/login`}
                  variant="body2"
                  sx={{ color: '#8B3040', fontWeight: 'bold' }}
                >
                  Return to Sign In
                </Link>
              </Box>
            </Paper>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="identity-page">
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography
              component="h1"
              variant="h4"
              align="center"
              sx={{
                fontFamily: 'Bitter, serif',
                color: '#8B3040',
                marginBottom: 3,
              }}
            >
              RentBeets
            </Typography>

            <Typography
              component="h2"
              variant="h5"
              align="center"
              sx={{ marginBottom: 1 }}
            >
              Forgot Password
            </Typography>

            <Typography
              variant="body2"
              align="center"
              color="text.secondary"
              sx={{ marginBottom: 3 }}
            >
              Enter your email address and we'll send you a link to reset your password
            </Typography>

            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  backgroundColor: '#8B3040',
                  '&:hover': {
                    backgroundColor: '#6d2533',
                  },
                }}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>

              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Link
                  href={`${identityUrl}/identity/login`}
                  variant="body2"
                  sx={{ color: '#8B3040' }}
                >
                  Back to Sign In
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </div>
  );
}

export default IdentityForgotPassword;
